import { Card } from "@/components/ui/card";
import { Expense } from "@/types/expense";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  IndianRupee, 
  Calendar, 
  Users, 
  ArrowUpCircle,
  Receipt,
  Tag
} from "lucide-react";
import { StatCard } from "./summary/StatCard";
import { RecentTransactions } from "./summary/RecentTransactions";
import { QuickStats } from "./summary/QuickStats";
import { ChartTabs } from "./summary/ChartTabs";

interface ExpenseSummaryProps {
  expenses: Expense[];
}

export const ExpenseSummary = ({ expenses }: ExpenseSummaryProps) => {
  const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
  
  const startDate = expenses[0]?.date ? new Date(expenses[0].date) : new Date();
  const endDate = expenses[expenses.length - 1]?.date ? new Date(expenses[expenses.length - 1].date) : new Date();
  const tripDuration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  const uniqueParticipants = new Set(expenses.flatMap(e => e.participants || []));
  const avgExpensePerDay = tripDuration > 0 ? totalExpenses / tripDuration : totalExpenses;
  
  const highestExpense = expenses.length > 0 
    ? expenses.reduce((max, exp) => Number(exp.amount) > Number(max.amount) ? exp : max)
    : null;
  
  const categoryTotals = expenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount);
    return acc;
  }, {} as Record<string, number>);
  
  const topCategory = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0];
  const recentTransactions = expenses.slice(-3).reverse();

  if (expenses.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="text-center space-y-4">
            <Receipt className="h-12 w-12 mx-auto text-primary animate-bounce" />
            <h2 className="text-xl font-semibold">No Expenses Added Yet</h2>
            <p className="text-muted-foreground">
              Start adding expenses to see your trip summary and insights.
            </p>
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-6 bg-gradient-to-br from-white to-primary/5 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={IndianRupee}
              iconColor="text-purple-500"
              title="Total Spent"
              value={`₹${totalExpenses.toFixed(2)}`}
              description="Total expenses across all categories"
              gradientFrom="purple-50"
              gradientTo="purple-100"
              delay={0.1}
            />
            <StatCard
              icon={Calendar}
              iconColor="text-blue-500"
              title="Trip Duration"
              value={`${tripDuration} days`}
              description="Length of your journey"
              gradientFrom="blue-50"
              gradientTo="blue-100"
              delay={0.2}
            />
            <StatCard
              icon={Users}
              iconColor="text-green-500"
              title="Participants"
              value={uniqueParticipants.size}
              description="People sharing expenses"
              gradientFrom="green-50"
              gradientTo="green-100"
              delay={0.3}
            />
            <StatCard
              icon={TrendingUp}
              iconColor="text-orange-500"
              title="Avg. per Day"
              value={`₹${avgExpensePerDay.toFixed(2)}`}
              description="Daily spending average"
              gradientFrom="orange-50"
              gradientTo="orange-100"
              delay={0.4}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <StatCard
              icon={ArrowUpCircle}
              iconColor="text-pink-500"
              title="Highest Expense"
              value={highestExpense ? `₹${Number(highestExpense.amount).toFixed(2)}` : "N/A"}
              description={highestExpense?.description || "No expenses yet"}
              gradientFrom="pink-50"
              gradientTo="pink-100"
              delay={0.5}
            />
            <StatCard
              icon={Tag}
              iconColor="text-yellow-500"
              title="Top Category"
              value={topCategory ? topCategory[0] : "N/A"}
              description={topCategory ? `₹${topCategory[1].toFixed(2)} spent` : "No expenses yet"}
              gradientFrom="yellow-50"
              gradientTo="yellow-100"
              delay={0.6}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <RecentTransactions transactions={recentTransactions} />
            <QuickStats 
              expenses={expenses}
              categoryTotals={categoryTotals}
              totalExpenses={totalExpenses}
            />
          </div>

          <ChartTabs expenses={expenses} totalExpenses={totalExpenses} />
        </Card>
      </motion.div>
    </div>
  );
};